import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortHeader, Sort, SortDirection } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';

import { LuicModule } from '@lohmann-birkner/luic';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, filter, Subscription, take } from 'rxjs';

import { FormioRendererI18n } from '../../components/data-interaction/formio-renderer/formio-renderer.component';
import { HomePatientBasicInfoComponent } from '../../components/home-page/home-patient-basic-info/home-patient-basic-info.component';
import { HomePatientTasksComponent } from '../../components/home-page/home-patient-tasks/home-patient-tasks.component';
import { HomePatientVisitComponent } from '../../components/home-page/home-patient-visit/home-patient-visit.component';
import { BreadcrumbComponent } from '../../components/utility/breadcrumb/breadcrumb.component';
import { SortIndicatorComponent } from '../../components/utility/sort-indicator/sort-indicator.component';
import { Settings } from '../../models/settings.model';
import {
  PatientListGroup,
  PatientListIdentifier,
  VcPatientListItem,
} from '../../models/view-content.models/view-content.model';
import { ViewContentCacheService } from '../../services/cache/view-content-cache.service';
import { AccessFacadeService } from '../../services/facades/access-facade.service';
import { SettingsFacadeService } from '../../services/facades/settings-facade.service';
import { LocatorService } from '../../services/locator.service';
import { ToolboxService } from '../../services/toolbox.service';

export interface SortOption {
  value: string;
  translateKey: string;
}

export interface SortSelection {
  value: SortOption['value'];
  sortOrder: SortDirection;
}

registerLocaleData(localeDe);

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  providers: [DatePipe],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'block' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'block' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    CommonModule,
    FormsModule,
    HomePatientBasicInfoComponent,
    HomePatientTasksComponent,
    HomePatientVisitComponent,
    LuicModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    RouterModule,
    SortIndicatorComponent,
    TranslateModule,
    MatSort,
    MatSortHeader,
    BreadcrumbComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild(MatTable) table: MatTable<any> | undefined;

  public caseLists: (PatientListGroup & {
    listIdentifier: PatientListIdentifier & { selected?: boolean };
  })[] = [];
  public allSubs: Subscription[] = [];
  public viewContentI18nVisiteRecord: FormioRendererI18n | undefined;
  public viewContentI18nTask: FormioRendererI18n | undefined;
  public currentToggleButton: string = 'patientList';
  public selectedList: PatientListGroup | undefined;
  public toggleState: boolean = false;
  public sortKey: string = 'lastName';
  public sortOrder: SortDirection = 'asc';
  public searchQuery: string = '';
  public sortOptions: SortOption[] = [
    { value: 'lastName', translateKey: 'COMPONENT.PAGE_WORKFLOW.lastName' },
    {
      value: 'admissionDate',
      translateKey: 'COMPONENT.PAGE_WORKFLOW.admissionDate',
    },
    { value: 'ward', translateKey: 'COMPONENT.PAGE_WORKFLOW.ward' },
  ];

  public sortSelection = signal<SortSelection>({
    value: 'lastName',
    sortOrder: 'asc',
  });

  public filteredPatients: VcPatientListItem[] = [];
  public patientListToShow: VcPatientListItem[] = [];
  public displayedColumns = ['patient', 'followUp', 'tasks'];
  public handyDisplayedColumns = ['patients'];
  columnsToDisplayWithExpand = [...this.handyDisplayedColumns, 'expand'];
  expandedElement: any;

  // Add activeFilter property
  public activeFilter: { type: string; value: string } | undefined;

  displayNameMap = new Map([
    [Breakpoints.HandsetPortrait, 'handsetPortrait'],
    [Breakpoints.HandsetLandscape, 'handsetLandscape'],
    [Breakpoints.Tablet, 'tablet'],
    [Breakpoints.Web, 'web'],
  ]);
  currentBreakpoint = signal('');

  public constructor(
    private accessFacade: AccessFacadeService,
    private breakpointObserver: BreakpointObserver,
    private settingsFacade: SettingsFacadeService,
    private locatorService: LocatorService,
    private tb: ToolboxService,
    private vcCache: ViewContentCacheService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.settingsFacade.loadSettings();
    this.locatorService.initializeLocators();
  }
  private viewedPatients: string[] = [];
  public bookmarkedPatients: string[] = [];

  public async ngOnInit(): Promise<void> {
    let dataLoaded = false;

    this.allSubs.push(
      this.vcCache.incomingViewContents$.pipe(debounceTime(500)).subscribe(async () => {
        await this.refresh();
        dataLoaded = true;

        const listName = this.route.snapshot.paramMap.get('listName');
        if (listName && this.caseLists.length > 0) {
          const listFromUrl = this.caseLists.find(list => list.listIdentifier.name === listName);

          if (listFromUrl) {
            this.onSelectList(listFromUrl, false);
          } else {
            console.warn(`List name '${listName}' not found after data refresh`);
          }
        }
      }),
    );

    this.allSubs.push(
      this.route.params.subscribe(params => {
        const listName = params['listName'];
        if (listName && this.caseLists.length > 0) {
          const listFromUrl = this.caseLists.find(list => list.listIdentifier.name === listName);

          if (listFromUrl) {
            this.onSelectList(listFromUrl, false);
          } else if (dataLoaded) {
            const caseInsensitiveMatch = this.caseLists.find(
              list => list.listIdentifier.name.toLowerCase() === listName.toLowerCase(),
            );

            if (caseInsensitiveMatch) {
              this.onSelectList(caseInsensitiveMatch, false);
            }
          }
        }
      }),
    );

    this.allSubs.push(
      this.breakpointObserver
        .observe([
          Breakpoints.HandsetPortrait,
          Breakpoints.HandsetLandscape,
          Breakpoints.Tablet,
          Breakpoints.Web,
        ])
        .subscribe(result => {
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              const breakpoint = this.displayNameMap.get(query) ?? '';
              this.currentBreakpoint.set(breakpoint);
            }
          }
        }),
    );

    this.allSubs.push(
      this.settingsFacade.settings$.subscribe(settings => {
        this.toggleState = settings.presentationMode;
        this.viewedPatients = settings.viewedPatients || [];
        this.bookmarkedPatients = settings.bookmarkedPatients || [];
      }),
    );

    this.allSubs.push(
      this.accessFacade.userTokens$.pipe(filter(e => !!e.token)).subscribe(async () => {
        await this.tb.executeInLoading(async () => await this.refresh());
      }),
    );

    this.sortSelection.set({
      value: this.sortKey,
      sortOrder: this.sortOrder,
    });
  }

  public ngOnDestroy(): void {
    this.allSubs.forEach(sub => sub.unsubscribe());
  }

  public onSelectList(
    list: PatientListGroup & {
      listIdentifier: PatientListIdentifier & { selected?: boolean };
    },
    updateUrl: boolean = true,
  ): void {
    if (!list) {
      console.warn('Attempted to select undefined list');
      return;
    }

    this.caseLists.forEach(l => {
      if (l.listIdentifier) {
        const wasSelected = l.listIdentifier.selected;
        l.listIdentifier.selected = l.listIdentifier.name === list.listIdentifier.name;

        if (wasSelected !== l.listIdentifier.selected) {
          console.debug(
            `Changed selection for ${l.listIdentifier.name}: ${wasSelected} -> ${l.listIdentifier.selected}`,
          );
        }
      }
    });

    this.selectedList = list;

    if (updateUrl) {
      this.router.navigate(['/start', list.listIdentifier.name]);
    }

    this.onSortChange({
      value: this.sortKey,
      sortOrder: this.sortOrder,
    });

    this.patientListToShow = this.selectedList?.patients ?? [];
  }

  public get displayText(): string {
    const option = this.sortOptions.find(option => option.value === this.sortSelection().value);
    return option ? option.translateKey : '';
  }

  public selectionChange(event: MatSelectChange): void {
    this.sortSelection.set({
      value: event.value,
      sortOrder: 'asc',
    });
    this.onSortChange(this.sortSelection());
  }

  public onSelectionChange(event: MatOptionSelectionChange): void {
    if (event.isUserInput && this.sortSelection().value === event.source.value) {
      this.sortSelection.update(value => ({
        ...value,
        sortOrder: value.sortOrder === 'asc' ? 'desc' : 'asc',
      }));
      this.onSortChange(this.sortSelection());
    }
  }

  public onSortChange(direction: SortSelection): void {
    this.sortKey = direction.value;
    this.sortOrder = direction.sortOrder;
    this.searchSortAndPaginate();
  }

  public sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.sortKey = 'lastName';
      this.sortOrder = 'asc';
      this.sortSelection.set({
        value: this.sortKey,
        sortOrder: this.sortOrder,
      });
      this.searchSortAndPaginate();
      return;
    }

    this.sortKey = sort.active;
    this.sortOrder = sort.direction;
    this.sortSelection.set({
      value: this.sortKey,
      sortOrder: this.sortOrder,
    });
    this.searchSortAndPaginate();
  }

  public onSearchFieldInput(): void {
    this.searchSortAndPaginate();
  }

  public onPresentationModeChange(state: boolean): void {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, presentationMode: state };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public handleToggle(state: boolean): void {
    this.toggleState = state;
    this.onPresentationModeChange(state);

    this.clearViewedPatients();

    this.searchSortAndPaginate();
  }

  public clearViewedPatients(): void {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, viewedPatients: [] };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public getSelectedListName(): string {
    const selectedList = this.caseLists.find(list => list.listIdentifier?.selected);
    return selectedList ? selectedList.listIdentifier.name : 'My Lists';
  }

  public isPatientViewed(patient: VcPatientListItem): boolean {
    return this.isPatientInArray(patient, this.viewedPatients);
  }

  public isPatientBookmarked(patient: VcPatientListItem): boolean {
    return this.isPatientInArray(patient, this.bookmarkedPatients);
  }

  public toggleBookmark(patient: VcPatientListItem, event: MouseEvent): void {
    event.stopPropagation();

    if (!patient?.patient_details?.case?.related_case_id) {
      return;
    }

    const patientId = patient.patient_details.case.related_case_id;

    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const bookmarkedPatients = [...(s.bookmarkedPatients || [])];
      const index = bookmarkedPatients.indexOf(patientId);

      if (index > -1) {
        bookmarkedPatients.splice(index, 1);
      } else {
        bookmarkedPatients.push(patientId);
      }

      const newSettings: Settings = { ...s, bookmarkedPatients };
      this.settingsFacade.saveSettings(newSettings);

      this.searchSortAndPaginate();
    });
  }

  private sortList(patients: VcPatientListItem[]): VcPatientListItem[] {
    return patients?.sort((a, b) => {
      const aIsBookmarked = this.isPatientBookmarked(a);
      const bIsBookmarked = this.isPatientBookmarked(b);

      if (aIsBookmarked !== bIsBookmarked) {
        return aIsBookmarked ? -1 : 1;
      }

      let compare = 0;

      switch (this.sortKey) {
        case 'lastName':
          compare =
            a.patient_details.patient.data.surname.localeCompare(
              b.patient_details.patient.data.surname,
            ) ||
            a.patient_details.patient.data.name.localeCompare(b.patient_details.patient.data.name);
          break;
        case 'admissionDate':
          compare =
            new Date(a.patient_details.case.data.admission_date).getTime() -
            new Date(b.patient_details.case.data.admission_date).getTime();
          break;
        case 'ward': {
          // Sort by service unit
          const getServiceUnitCode = (p: VcPatientListItem) => {
            return p.patient_details.current_place.data.room?.service_unit?.code || '';
          };

          const serviceUnitCodeA = getServiceUnitCode(a);
          const serviceUnitCodeB = getServiceUnitCode(b);
          compare = serviceUnitCodeA.localeCompare(serviceUnitCodeB);

          // Sort by room number
          if (compare === 0) {
            const roomCodeA = a.patient_details.current_place.data.code || '';
            const roomCodeB = b.patient_details.current_place.data.code || '';
            compare = roomCodeA.localeCompare(roomCodeB);
          }

          break;
        }
        case 'patient':
          compare =
            a.patient_details.patient.data.surname.localeCompare(
              b.patient_details.patient.data.surname,
            ) ||
            a.patient_details.patient.data.name.localeCompare(b.patient_details.patient.data.name);
          break;
        case 'followUp':
          compare =
            a.visit_record?.data.date_and_time?.localeCompare(
              b.visit_record?.data.date_and_time ?? '',
            ) ?? 0;
          break;
        case 'tasks':
          compare = a.tasks?.[0]?.data.goal_date.localeCompare(b.tasks?.[0]?.data.goal_date);
          break;

        default:
          break;
      }

      return this.sortOrder === 'asc' ? compare : -compare;
    });
  }

  private matchesPatient(patient: VcPatientListItem, query: string): boolean {
    const lowerCaseQuery = query.toLowerCase();

    const matchesSimpleFields = [
      patient.patient_details?.patient?.data?.name ?? '',
      patient.patient_details?.patient?.data?.surname ?? '',
      patient.patient_details?.current_place?.data?.room?.long_text ?? '',
      patient.patient_details?.case?.data?.admission_date ?? '',
    ].some(field => field.toLowerCase().includes(lowerCaseQuery));

    const matchesDiagnosis =
      patient.patient_details?.last_diagnosis?.data?.diagnose
        ?.toLowerCase()
        .includes(lowerCaseQuery) ?? false;

    const matchesTasks =
      patient.tasks?.some(
        (task: any) =>
          task.data?.task_name?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.details?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.priority?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.editor?.surname?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.editor?.name?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.goalDateOn?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.contractor?.surname?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.contractor?.name?.toLowerCase().includes(lowerCaseQuery),
      ) ?? false;

    const matchesVisits =
      patient.visit_record?.history?.some((visitRecord: any) =>
        visitRecord.data?.note?.toLowerCase().includes(lowerCaseQuery),
      ) ?? false;

    const matchesDischarge =
      patient.patient_details?.case?.data?.discharge_date?.toLowerCase().includes(lowerCaseQuery) ||
      patient.patient_details?.case?.data?.planned_discharge_date
        ?.toLowerCase()
        .includes(lowerCaseQuery) ||
      false;

    return (
      matchesSimpleFields || matchesDiagnosis || matchesTasks || matchesVisits || matchesDischarge
    );
  }

  private async refresh(): Promise<void> {
    try {
      const fetchedLists = await this.vcCache.getCaseList();

      const currentListName = this.route.snapshot.paramMap.get('listName');

      this.caseLists = fetchedLists.map((list, index) => ({
        ...list,
        listIdentifier: {
          ...list.listIdentifier,
          selected: currentListName ? list.listIdentifier.name === currentListName : index === 0,
        },
      }));
      let selectedList = currentListName
        ? this.caseLists.find(list => list.listIdentifier.name === currentListName)
        : null;

      if (!selectedList && currentListName) {
        selectedList = this.caseLists.find(
          list => list.listIdentifier.name.toLowerCase() === currentListName.toLowerCase(),
        );

        if (selectedList) {
          console.debug(
            `Found case-insensitive match for "${currentListName}": "${selectedList.listIdentifier.name}"`,
          );
        }
      }

      this.selectedList = selectedList || this.caseLists[0];

      if (this.selectedList) {
        this.caseLists.forEach(list => {
          list.listIdentifier.selected =
            list.listIdentifier.name === this.selectedList?.listIdentifier.name;
        });
      }

      this.searchSortAndPaginate();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }

  private searchSortAndPaginate(): void {
    if (!this.selectedList) return;

    this.filteredPatients = !this.searchQuery.trim()
      ? this.selectedList.patients
      : this.selectedList.patients.filter(patient =>
          this.matchesPatient(patient, this.searchQuery.toLowerCase()),
        );

    this.patientListToShow = this.sortList(this.filteredPatients);
    this.table?.renderRows();
  }

  private isPatientInArray(patient: VcPatientListItem, arrayToCheck: string[]): boolean {
    if (!patient?.patient_details?.case?.related_case_id) {
      return false;
    }

    const patientId = patient.patient_details.case.related_case_id;
    return Array.isArray(arrayToCheck) && arrayToCheck.includes(patientId);
  }

  public getSelectedListId(): string {
    const selectedList = this.caseLists.find(list => list.listIdentifier?.selected);
    return selectedList ? selectedList.listIdentifier.name : '';
  }

  public onListSelectionChange(event: MatSelectChange): void {
    const selectedList = this.caseLists.find(list => list.listIdentifier.name === event.value);

    if (selectedList) {
      this.onSelectList(selectedList, true);
    }
  }
}
